import filter from 'lodash/filter'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import get from 'lodash/get'
import isInteger from 'lodash/isInteger'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import some from 'lodash/some'
import toNumber from 'lodash/toNumber'
import trim from 'lodash/trim'
import type {
  DirectionEnum,
  PivotCondition,
  PivotLayout,
  PivotLayoutAggregatedMeasure,
  PivotLayoutField,
  PivotLayoutFilter,
} from '@ztechapps/common.contract'
import type { PivotFilterSetting, PivotMember } from './types'
import type { PivotOperandProblems } from './pivotFilterOperator.utils'
import {
  PivotFieldTypeEnum,
  PivotFilterKindEnum,
  PivotFilterOperatorEnum,
  PivotRelativeUnitEnum,
  PivotValueFilterDirectionEnum,
  PivotValueFilterModeEnum,
  PivotValueFilterScopeEnum,
} from '@ztechapps/common.contract'
import { isPivotCalculatedMeasure, isPivotRelativeOperator, isPivotTopOrBottomOperator } from '@ztechapps/common.utils'
import { PivotFilterSectionEnum } from '../FieldPanel/PivotFilterFields'
import { createPivotMemberMatcher } from './createPivotMemberMatcher'
import {
  PIVOT_CONDITION_OPERATOR_OPTIONS,
  findPivotOperandProblems,
  toPivotOperandValues,
} from './pivotFilterOperator.utils'
import { findPivotLayoutItem } from './pivotLayout.utils'

/** What the filter dialog stages for one field until Apply — a draft of each kind, so switching kind loses nothing. */
export type PivotFilterFormValues = {
  kind: PivotFilterKindEnum
  sort: DirectionEnum | ''
  selection: { search: string; unchecked: string[]; rangeFrom: string; rangeTo: string }
  condition: { operator: PivotFilterOperatorEnum; value1: string; value2: string; unit: PivotRelativeUnitEnum }
  value: {
    measure: string
    operator: PivotFilterOperatorEnum
    value1: string
    value2: string
    mode: PivotValueFilterModeEnum
    count: string
    scope: PivotValueFilterScopeEnum
  }
}

type PivotFilterLayout = Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>

/** The measures a Value filter can rank by — every aggregated value of the layout. */
export const listPivotRankMeasures = (layout: PivotFilterLayout): PivotLayoutAggregatedMeasure[] =>
  filter(layout.measures, (measure): measure is PivotLayoutAggregatedMeasure => !isPivotCalculatedMeasure(measure))

/** A ranked measure is its field and its aggregation together, the way Flexmonster's value filter names it. */
export const toPivotMeasureKey = (measure: { field: string; aggregation: string }): string =>
  `${measure.field}|${measure.aggregation}`

/** The field a Rows or Columns field ranks inside — the one before it on its axis, none for the outermost. */
export const findPivotRankParent = (
  layout: PivotFilterLayout,
  field: PivotLayoutField
): PivotLayoutField | undefined => {
  const axis = findPivotLayoutItem(layout.rows, field) ? layout.rows : layout.columns
  return axis[findIndex(axis, (item) => item.field === field.field && item.dateGroup === field.dateGroup) - 1]
}

const DEFAULT_COUNT = '10'

/** Builds the Condition the staged inputs stand for, or the problems keeping it from being applied. */
export const buildConditionFilter = ({
  operator,
  value1,
  value2,
  unit,
  valueType,
}: PivotFilterFormValues['condition'] & { valueType: PivotFieldTypeEnum }):
  | { condition: PivotCondition; problems?: undefined }
  | { condition?: undefined; problems: PivotOperandProblems } => {
  const problems = findPivotOperandProblems({ operator, value1, value2, valueType })
  if (problems.value1 || problems.value2) return { problems }
  return {
    condition: {
      operator,
      values: toPivotOperandValues(operator, value1, value2),
      unit: isPivotRelativeOperator(operator) ? unit : undefined,
    },
  }
}

/** Builds the staged values from the field's current filter. */
export const toPivotFilterFormValues = ({
  entry,
  activeKind,
  members,
  fieldType,
  layout,
  sortDirection,
}: {
  entry?: PivotLayoutFilter
  activeKind?: PivotFilterKindEnum
  members: PivotMember[]
  fieldType?: PivotFieldTypeEnum
  layout: PivotFilterLayout
  sortDirection?: DirectionEnum
}): PivotFilterFormValues => {
  const matches = createPivotMemberMatcher({
    includedValues: entry?.includedValues,
    excludedValues: entry?.excludedValues,
  })
  const condition = entry?.conditionFilter
  const valueFilter = entry?.valueFilter
  const comparison = entry?.valueComparison
  const firstMeasure = listPivotRankMeasures(layout)[0]
  const measure = valueFilter?.measure ?? comparison?.measure ?? firstMeasure
  return {
    kind: activeKind ?? PivotFilterKindEnum.Selection,
    sort: sortDirection ?? '',
    selection: {
      search: '',
      unchecked: map(
        filter(members, (member) => !matches(member.key)),
        'key'
      ),
      rangeFrom: entry?.dateRange?.from ?? '',
      rangeTo: entry?.dateRange?.to ?? '',
    },
    condition: {
      operator:
        condition?.operator ?? PIVOT_CONDITION_OPERATOR_OPTIONS[fieldType ?? PivotFieldTypeEnum.String][0].value,
      value1: condition?.values[0] ?? '',
      value2: condition?.values[1] ?? '',
      unit: condition?.unit ?? PivotRelativeUnitEnum.month,
    },
    value: {
      measure: measure ? toPivotMeasureKey(measure) : '',
      operator:
        comparison?.operator ??
        (valueFilter?.direction === PivotValueFilterDirectionEnum.bottom
          ? PivotFilterOperatorEnum.bottom
          : PivotFilterOperatorEnum.top),
      value1: comparison?.values[0] ?? '',
      value2: comparison?.values[1] ?? '',
      mode: valueFilter?.mode ?? PivotValueFilterModeEnum.items,
      count: String(valueFilter?.value ?? DEFAULT_COUNT),
      scope: valueFilter?.scope ?? PivotValueFilterScopeEnum.overall,
    },
  }
}

/** Turns the staged values of the chosen kind into what the layout stores. */
export const toPivotFilterSetting = ({
  values,
  layout,
  field,
  fieldType,
}: {
  values: PivotFilterFormValues
  layout: PivotFilterLayout
  field: PivotLayoutField
  fieldType: PivotFieldTypeEnum
}): PivotFilterSetting => {
  if (values.kind === PivotFilterKindEnum.Selection) {
    const { unchecked, rangeFrom, rangeTo } = values.selection
    return {
      kind: values.kind,
      excludedValues: unchecked,
      dateRange: rangeFrom && rangeTo ? { from: rangeFrom, to: rangeTo } : undefined,
    }
  }
  if (values.kind === PivotFilterKindEnum.Condition) {
    const { condition } = buildConditionFilter({ ...values.condition, valueType: fieldType })
    // validation keeps a Condition with a missing or invalid operand from being applied
    if (!condition) throw new Error('A Condition needs valid operands')
    return { kind: values.kind, condition }
  }
  const { measure: measureKey, operator, value1, value2, mode, count, scope } = values.value
  const measure = find(listPivotRankMeasures(layout), (item) => toPivotMeasureKey(item) === measureKey)
  // validation keeps a Value filter without a measure from being applied
  if (!measure) throw new Error('A Value filter needs a measure to rank by')
  const ranked = { field: measure.field, aggregation: measure.aggregation }
  if (isPivotTopOrBottomOperator(operator))
    return {
      kind: values.kind,
      valueFilter: {
        measure: ranked,
        direction:
          operator === PivotFilterOperatorEnum.top
            ? PivotValueFilterDirectionEnum.top
            : PivotValueFilterDirectionEnum.bottom,
        mode,
        value: toNumber(count),
        scope: findPivotRankParent(layout, field) ? scope : PivotValueFilterScopeEnum.overall,
      },
    }
  return {
    kind: values.kind,
    valueComparison: {
      measure: ranked,
      operator,
      values: toPivotOperandValues(operator, trim(value1), trim(value2)),
    },
  }
}

export type PivotFilterFormErrors = {
  condition?: { value1?: string; value2?: string }
  value?: { measure?: string; count?: string; value1?: string; value2?: string }
}

/** Finds what keeps the chosen kind from being applied — the other kinds' drafts are never checked. */
export const validatePivotFilterForm = (
  values: PivotFilterFormValues,
  fieldType: PivotFieldTypeEnum
): PivotFilterFormErrors => {
  const errors: PivotFilterFormErrors = {}
  if (values.kind === PivotFilterKindEnum.Condition) {
    const { problems } = buildConditionFilter({ ...values.condition, valueType: fieldType })
    if (problems) errors.condition = problems
    return errors
  }
  if (values.kind === PivotFilterKindEnum.Value) {
    const { measure, operator, value1, value2, mode, count } = values.value
    const problems: NonNullable<PivotFilterFormErrors['value']> = {}
    if (isEmpty(measure)) problems.measure = 'Pick a value to rank by'
    if (isPivotTopOrBottomOperator(operator)) {
      const total = toNumber(count)
      const isPercent = mode === PivotValueFilterModeEnum.percent
      if (!isInteger(total) || total < 1 || (isPercent && total > 100))
        problems.count = isPercent ? 'Enter 1 to 100' : 'Enter a whole number of at least 1'
    } else {
      Object.assign(
        problems,
        findPivotOperandProblems({ operator, value1, value2, valueType: PivotFieldTypeEnum.Number })
      )
    }
    if (Object.values(problems).some(Boolean)) errors.value = problems
  }
  return errors
}

/**
 * Whether the chosen kind has an error on screen — a field shows its error once touched, or once Apply was tried. Only
 * the chosen kind counts: a kind switch skips validation, so another kind's errors stay behind, out of sight.
 */
export const hasVisiblePivotFilterError = ({
  kind,
  errors,
  touched,
  submitCount,
}: {
  kind: PivotFilterKindEnum
  errors: PivotFilterFormErrors
  touched: object
  submitCount: number
}): boolean => {
  const section =
    kind === PivotFilterKindEnum.Condition
      ? PivotFilterSectionEnum.condition
      : kind === PivotFilterKindEnum.Value
        ? PivotFilterSectionEnum.value
        : undefined
  return (
    section !== undefined &&
    some(errors[section], (error, name) => !!error && (submitCount > 0 || !!get(touched, [section, name])))
  )
}
